import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IonPage, useIonViewDidEnter } from '@ionic/react';
import {
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  Container_v2 as Container,
  ScreenInput,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import { PublicRoutes } from '../../../routes/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { authNameSelector, isWebToWebAuthSelector, signUpEmailSelector } from '../../../redux/auth/selector';
import { RouteComponentProps } from 'react-router';
import { setSignUpName } from '../../../redux/auth/reducer';
import { useAnalytics, useAuthFlow, useIsKeyBoardShown } from '../../../shared';
import { AuthPageHeaderFeature } from 'src/features/AuthPageHeaderFeature';

const NamePage: FC<RouteComponentProps> = ({ history }) => {
  const { t } = useTranslation('auth');
  const dispatch = useDispatch();
  const isKeyboardShown = useIsKeyBoardShown();
  const { trackPredefinedEvent } = useAnalytics();
  const { authUser } = useAuthFlow();

  const nameRef = useRef<HTMLInputElement | null>(null);

  const signUpEmail = useSelector(signUpEmailSelector);
  const nameData = useSelector(authNameSelector);
  const isWebToWebAuth = useSelector(isWebToWebAuthSelector);

  const [isDisabled, setIsDisabled] = useState(true);

  useIonViewDidEnter(() => {
    if (!signUpEmail) {
      const webToWebAuth = localStorage.getItem('webToWebAuth');

      if (webToWebAuth) {
        history.push(PublicRoutes.AUTH);
      } else {
        history.push(PublicRoutes.EMAIL_SIGN_IN);
      }
      return;
    }
  }, [signUpEmail, history]);

  useIonViewDidEnter(() => {
    trackPredefinedEvent('SIGNUP_USER_INFO_VIEWED', {
      screen_type: 'first_name',
    });
  });

  useIonViewDidEnter(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, [nameRef]);

  useEffect(() => {
    setIsDisabled(!nameData?.firstName?.length);
  }, [nameData]);

  const handleNameSubmit = useCallback(() => {
    if (isDisabled) return;

    trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
      screen_type: 'first_name',
      value: nameData.firstName,
    });
    history.push(PublicRoutes.LAST_NAME_SIGN_UP);
  }, [history, isDisabled, nameData.firstName, trackPredefinedEvent]);

  const redirectTo = useMemo(() => {
    if (authUser?.email) {
      return undefined;
    } else if (isWebToWebAuth || localStorage.getItem('webToWebAuth')) {
      return PublicRoutes.AUTH;
    } else {
      return PublicRoutes.EMAIL_SIGN_IN;
    }
  }, [isWebToWebAuth, authUser?.email]);

  return (
    <IonPage>
      <AuthPageHeaderFeature backButtonLink={redirectTo} />
      <Container isFlexContainer>
        <Typography variant={TypographyVariantsEnum.Title28Semi}>{t('auth.create.name.title')}</Typography>
        <ScreenInput
          ref={nameRef}
          placeholder={t('auth.create.name.placeholder')}
          value={nameData.firstName}
          type={'text'}
          maxLength={50}
          onChange={(e) => dispatch(setSignUpName({ firstName: e.target.value }))}
          onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
        />
      </Container>
      <AppFooter transparent bottomSaveArea={!isKeyboardShown}>
        <Button disabled={isDisabled} fullWidth size={'large'} onClick={handleNameSubmit}>
          {t('auth.button.next')}
        </Button>
      </AppFooter>
    </IonPage>
  );
};

export default NamePage;
